import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Target,
  Eye,
  Award,
  Factory,
  ShieldCheck,
  Zap,
  Wrench,
  Users,
} from "lucide-react";
import { COMPANY } from "@/data/company";
import { Button } from "@/components/ui/button";
import { IMGS, onImgError } from "@/lib/images";

const TIMELINE = [
  { year: "1981", label: "Founded in Chennai" },
  { year: "2008", label: "ISO 9001 certification" },
  { year: "2010", label: "50 kVA to 1000 kVA product line launched" },
  { year: "2014", label: "In-house R&D + IoT-ready product line" },
  { year: "2016", label: "CE Certification & export markets" },
  { year: "2020", label: "12,500+ installations across India" },
];

export default function About({ onOpenEnquiry }) {
  return (
    <>
      <Helmet>
        <title>About Us — Vortex Power Solutions</title>
        <meta
          name="description"
          content="Vortex Power Solutions — 43+ years of engineering excellence in industrial power protection. ISO 9001, CE certified, Made in India."
        />
        <link rel="canonical" href="https://www.vortexworld.in/about" />
      </Helmet>

      {/* Hero — lighter tint */}
      <section className="relative bg-vortex-tint text-vortex-navy overflow-hidden">
        <div className="absolute inset-0 grid-pattern-light opacity-70 pointer-events-none" />
        <div className="container-wide relative py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="section-label mb-5">
              Est. 1981 · Chennai, India
            </div>
            <h1 className="font-heading font-extrabold text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.05] mb-6 text-balance">
              4 decades of powering India&apos;s critical loads.
            </h1>
            <p className="text-lg md:text-xl text-vortex-slate leading-relaxed max-w-2xl">
              Vortex Power Solutions is a Chennai-headquartered manufacturer of
              industrial power conditioning equipment — engineered for
              reliability, built for uptime.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="bg-white py-24">
        <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="section-label mb-4">Our Story</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-vortex-navy tracking-tighter leading-[1.05] mb-6">
              Engineered by engineers, for engineers.
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-vortex-slate text-lg leading-relaxed">
            <p>
              Founded in 1981 by a team of power electronics engineers, Vortex
              began with a simple mission: build industrial power products that
              never fail — even in the harshest Indian conditions.
            </p>
            <p>
              Today, our more than 5000+ manufacturing facility in Madipakkam
              produces over 500 units every month, ranging from 3 kVA to 3000
              kVA. Our in-house R&amp;D team, with 8 senior electronics
              engineers, continuously refines core designs and control
              algorithms.
            </p>
            <p>
              We work directly with facility managers, procurement heads and
              consulting engineers — no middlemen, no compromises. Every unit
              ships with a signed test certificate.
            </p>
          </div>
        </div>
      </section>

      {/* Vision / Mission */}
      <section className="bg-vortex-alt py-24">
        <div className="container-wide grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              Icon: Target,
              title: "Our Mission",
              text:
                "To deliver world-class power conditioning products that protect our customers' investments, empower Indian manufacturing and set new benchmarks in reliability.",
            },
            {
              Icon: Eye,
              title: "Our Vision",
              text:
                "To be India's most trusted brand in industrial power quality — recognised globally for engineering excellence and customer obsession.",
            },
            {
              Icon: ShieldCheck,
              title: "Quality Policy",
              text:
                "Zero-defect manufacturing, 100% pre-dispatch testing, and continuous improvement across every process. ISO 9001:2015 certified.",
            },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.6 }}
              className="bg-white rounded-2xl p-8 border border-vortex-border hover:border-vortex-blue/40 transition-colors"
              data-testid={`about-value-${i}`}
            >
              <div className="w-12 h-12 rounded-xl bg-vortex-navy text-vortex-blue flex items-center justify-center mb-5">
                <c.Icon className="w-5 h-5" strokeWidth={1.5} />
              </div>
              <h3 className="font-heading font-bold text-2xl text-vortex-navy mb-3">
                {c.title}
              </h3>
              <p className="text-vortex-slate leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Manufacturing */}
      <section className="bg-white py-24">
        <div className="container-wide">
          <div className="max-w-2xl mb-14">
            <div className="section-label mb-4">Manufacturing Excellence</div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl text-vortex-navy tracking-tighter leading-[1.05]">
              Inside our Chennai factory.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {[IMGS.factory1, IMGS.factory2, IMGS.factory3].map((src, i) => (
              <div
                key={i}
                className="aspect-[4/3] rounded-2xl overflow-hidden bg-vortex-alt border border-vortex-border group"
              >
                <img
                  src={src}
                  onError={onImgError}
                  alt="Factory floor"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { Icon: Factory, label: "45,000 sq. ft. facility" },
              { Icon: Wrench, label: "In-house winding & assembly" },
              { Icon: Zap, label: "100% load-tested" },
              { Icon: Users, label: "80+ trained technicians" },
            ].map((c, i) => (
              <div
                key={i}
                className="bg-vortex-alt border border-vortex-border rounded-2xl p-5 flex items-center gap-3"
              >
                <c.Icon
                  className="w-6 h-6 text-vortex-blue flex-shrink-0"
                  strokeWidth={1.5}
                />
                <span className="font-semibold text-vortex-navy text-sm">
                  {c.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-vortex-navy text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
        <div className="container-wide relative">
          <div className="max-w-2xl mb-14">
            <div className="section-label !text-vortex-blue !border-vortex-blue/40 mb-4">
              Our Journey
            </div>
            <h2 className="font-heading font-extrabold text-4xl md:text-5xl tracking-tighter leading-[1.05]">
              20 years of engineering firsts.
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/20" />
            <ul className="space-y-8">
              {TIMELINE.map((t, i) => (
                <motion.li
                  key={t.year}
                  initial={{ opacity: 0, x: i % 2 ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6 }}
                  className={`relative flex items-start md:justify-between gap-6 ${
                    i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  <div className="hidden md:block w-1/2" />
                  <div className="absolute left-4 md:left-1/2 top-1 -translate-x-1/2 w-3 h-3 rounded-full bg-vortex-orange ring-4 ring-vortex-navy" />
                  <div className="pl-12 md:pl-0 md:w-1/2">
                    <div className="font-heading font-extrabold text-3xl text-vortex-blue">
                      {t.year}
                    </div>
                    <div className="text-white/85 mt-1">{t.label}</div>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20">
        <div className="container-wide text-center max-w-2xl mx-auto">
          <div className="w-14 h-14 rounded-2xl bg-vortex-alt border border-vortex-border flex items-center justify-center mx-auto mb-6">
            <Award className="w-6 h-6 text-vortex-blue" strokeWidth={1.5} />
          </div>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-vortex-navy tracking-tight mb-4">
            Ready to work with Vortex?
          </h2>
          <p className="text-vortex-slate mb-8">
            Talk to our senior power engineers today.
          </p>
          <Button
            onClick={onOpenEnquiry}
            className="rounded-full bg-vortex-orange hover:bg-vortex-orange-deep text-white h-12 px-8 font-semibold"
            data-testid="about-cta-btn"
          >
            Request a Quote
          </Button>
        </div>
      </section>
    </>
  );
}
